"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pieces = [{
  number: "01",
  title: "Global Brand Launch",
  year: "2024",
  joint: "Creative Direction",
  material: "Full-Scale Production",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/brand_launch_campaign.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/brand_launch_campaign.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/brand_launch_campaign.webp-wvc-sizes",
  alt: "High-end commercial campaign featuring cinematic lighting and bold brand identity"
}, {
  number: "02",
  title: "Digital Storytelling",
  year: "2024",
  joint: "Brand Narrative",
  material: "Social-First Content",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/digital_storytelling_spot.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/digital_storytelling_spot.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/digital_storytelling_spot.png-wvc-sizes",
  alt: "Dynamic social media content piece with fast-paced editing and motion graphics"
}, {
  number: "03",
  title: "Product Reveal Spot",
  year: "2023",
  joint: "Visual Strategy",
  material: "Commercial Video",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/product_reveal_spot.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/product_reveal_spot.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/product_reveal_spot.png-wvc-sizes",
  alt: "Sleek product commercial showcasing high-fidelity cinematography"
}, {
  number: "04",
  title: "Immersive Experience",
  year: "2023",
  joint: "Concept Design",
  material: "Experiential Media",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/immersive_experience_media.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/immersive_experience_media.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/immersive_experience_media.png-wvc-sizes",
  alt: "Large scale immersive media installation for global brand event"
}, {
  number: "05",
  title: "Social Scaling Series",
  year: "2024",
  joint: "Content Strategy",
  material: "Multi-Platform Assets",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/social_scaling_series.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/social_scaling_series.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/social_scaling_series.png-wvc-sizes",
  alt: "Series of high-impact social assets designed for rapid platform scaling"
}, {
  number: "06",
  title: "Cinematic Brand Film",
  year: "2023",
  joint: "Script Treatment",
  material: "Cinematic Production",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/cinematic_brand_film.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/cinematic_brand_film.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/cinematic_brand_film.png-wvc-sizes",
  alt: "Cinematic brand film showcasing emotional storytelling and high-end production"
}];
export default function FromConceptToScreen() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const footer = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="4145" id="from-concept-to-screen" className="bg-background text-foreground py-20 md:py-32 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={header.ref} className={`grid grid-cols-12 gap-6 md:gap-8 mb-16 md:mb-24 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="col-span-12 mb-4">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
              — The Vivid Portfolio / Volume I
            </span>
          </div>

          <h2 className="font-default col-span-12 md:col-span-7 text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tighter text-foreground">
            Campaigns built<br />
            <span className="italic text-primary">for impact</span>
          </h2>

          <div className="col-span-12 md:col-span-4 md:col-start-9 flex items-end">
            <p className="font-default text-sm md:text-base text-muted-foreground leading-relaxed">
              A curated showcase of end-to-end creative production by Vivid Studio—where initial concept mood boards meet high-end commercial execution.
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {pieces.map((piece, i) => {
          const layouts = ["col-span-12 md:col-span-7 md:row-span-2 aspect-[4/5]", "col-span-12 md:col-span-5 md:mt-16 aspect-[4/3]", "col-span-12 md:col-span-4 md:col-start-2 aspect-square", "col-span-12 md:col-span-7 md:col-start-6 aspect-[16/9]", "col-span-12 md:col-span-4 aspect-[3/4]", "col-span-12 md:col-span-8 md:col-start-5 aspect-[5/3]"];
          return <figure key={i} data-index={i} className={`group relative overflow-hidden rounded-lg bg-card border border-border ${layouts[i]}`}>
                <img src={piece.image} data-wvc-srcset={piece.srcset} data-wvc-sizes={piece.sizes} alt={piece.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />

                {}
                <figcaption className="absolute top-5 left-5 md:top-6 md:left-6 z-10">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/90 bg-background/40 backdrop-blur-sm px-3 py-1.5 rounded-md">
                    — {piece.number} / {piece.title} / {piece.year}
                  </span>
                </figcaption>

                {}
                <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-700 ease-out">
                  <div className="bg-gradient-to-t from-background via-background/95 to-transparent pt-16 pb-7 px-6 md:px-8">
                    <h3 className="font-default text-2xl md:text-3xl font-semibold italic text-foreground leading-tight">
                      {piece.title}
                    </h3>
                    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.18em]">
                      <span className="text-muted-foreground">
                        <span className="text-primary">Year</span> · {piece.year}
                      </span>
                      <span className="text-muted-foreground">
                        <span className="text-primary">Focus</span> ·{" "}
                        {piece.joint}
                      </span>
                      <span className="text-muted-foreground w-full md:w-auto">
                        <span className="text-primary">Scope</span> ·{" "}
                        {piece.material}
                      </span>
                    </div>
                  </div>
                </div>
              </figure>;
        })}
        </div>

        {}
        <div ref={footer.ref} className={`mt-20 md:mt-28 flex justify-center transition-all duration-1000 ease-out ${footer.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Button asChild variant="link" className="group font-default text-lg md:text-xl italic text-foreground hover:text-primary no-underline hover:no-underline px-0">
            <Link to="/OurWorkPage">
              <span className="relative">
                View our full production portfolio
                <span className="absolute left-0 -bottom-1 h-px w-full bg-primary/60 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
              </span>
              <ArrowRight className="ml-3 size-5 text-primary transition-transform duration-500 group-hover:translate-x-2" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}